import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, Save, Truck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateGatePassPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Master data
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [uomOptions, setUomOptions] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    category: '',
    location_id: '',
    party_type: 'other',
    party_id: '',
    party_name: '',
    requires_return: false,
    expected_return_date: '',
    vehicle_number: '',
    driver_name: '',
    driver_contact: '',
    transporter_name: '',
    dc_number: '',
    remarks: ''
  });
  
  // Items
  const [items, setItems] = useState([
    { item_name: '', quantity: '', unit: 'NOS', remarks: '' }
  ]);

  const fetchMasterData = useCallback(async () => {
    if (!token) {
      console.log('Token not available yet, skipping fetch');
      return;
    }
    setLoading(true);
    try {
      const [catRes, locRes, suppRes, clientRes, uomRes] = await Promise.all([
        fetch(`${API_URL}/api/gate-passes/metadata/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/locations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/suppliers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/uom/options`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (catRes.ok) {
        setCategories(await catRes.json());
      }
      if (locRes.ok) {
        setLocations(await locRes.json());
      }
      if (suppRes.ok) {
        setSuppliers(await suppRes.json());
      }
      if (clientRes.ok) {
        setClients(await clientRes.json());
      }
      if (uomRes.ok) {
        const uomData = await uomRes.json();
        setUomOptions(uomData.codes || []);
      } else {
        console.warn('UOM API returned status:', uomRes.status);
        // Fallback UOM options
        setUomOptions(['NOS', 'KG', 'MTR', 'SET', 'LTR']);
      }
    } catch (err) {
      console.error('Failed to fetch master data:', err);
      toast.error('Failed to load form data');
      // Set fallback UOM options
      setUomOptions(['NOS', 'KG', 'MTR', 'SET', 'LTR']);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  // Auto-set requires_return based on category
  useEffect(() => {
    if (formData.category) {
      const cat = categories.find(c => c.value === formData.category);
      if (cat) {
        setFormData(prev => ({
          ...prev,
          requires_return: cat.is_returnable
        }));
      }
    }
  }, [formData.category, categories]);

  // Auto-set party_type based on category
  useEffect(() => {
    const categoryPartyTypeMap = {
      sales_dispatch: 'customer',
      purchase_return: 'supplier',
      inter_unit_transfer: 'unit',
      sample_outward: 'customer',
      supplier_sample_return: 'supplier',
      sales_replacement: 'customer',
      job_work_ojc: 'job_worker',
      repair_amc: 'supplier',
      scrap_disposal: 'other',
      exhibition_demo: 'customer',
      loan_rental: 'customer',
      personal: 'employee'
    };
    
    if (formData.category) {
      const defaultPartyType = categoryPartyTypeMap[formData.category] || 'other';
      setFormData(prev => ({
        ...prev,
        party_type: defaultPartyType,
        party_id: '',
        party_name: ''
      }));
    }
  }, [formData.category]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePartySelect = (partyId) => {
    if (formData.party_type === 'supplier') {
      const supplier = suppliers.find(s => s.id === partyId);
      if (supplier) {
        setFormData(prev => ({
          ...prev,
          party_id: supplier.id,
          party_name: supplier.name
        }));
      }
    } else if (formData.party_type === 'customer') {
      const client = clients.find(c => c.id === partyId);
      if (client) {
        setFormData(prev => ({
          ...prev,
          party_id: client.id,
          party_name: client.name
        }));
      }
    } else if (formData.party_type === 'unit') {
      const location = locations.find(l => l.id === partyId);
      if (location) {
        setFormData(prev => ({
          ...prev,
          party_id: location.id,
          party_name: location.name
        }));
      }
    }
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };
      return newItems;
    });
  };

  const addItem = () => {
    setItems(prev => [...prev, { item_name: '', quantity: '', unit: 'NOS', remarks: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.location_id) {
      toast.error('Please select a location');
      return;
    }
    if (!formData.party_name.trim()) {
      toast.error('Please enter party name');
      return;
    }
    
    const validItems = items.filter(item => item.item_name.trim() && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        category: formData.category,
        location_id: formData.location_id,
        party_type: formData.party_type,
        party_id: formData.party_id || null,
        party_name: formData.party_name,
        requires_return: formData.requires_return,
        expected_return_date: formData.expected_return_date || null,
        vehicle_number: formData.vehicle_number || null,
        driver_name: formData.driver_name || null,
        driver_contact: formData.driver_contact || null,
        transporter_name: formData.transporter_name || null,
        dc_number: formData.dc_number || null,
        remarks: formData.remarks || null,
        items: validItems.map(item => ({
          item_name: item.item_name,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          remarks: item.remarks || null
        }))
      };
      
      const res = await fetch(`${API_URL}/api/gate-passes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Gate Pass ${data.gate_pass_number} created successfully`);
        navigate('/store/gate-pass');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create gate pass');
      }
    } catch (err) {
      toast.error('Failed to create gate pass');
    } finally {
      setSaving(false);
    }
  };

  const getPartyOptions = () => {
    let options = [];
    if (formData.party_type === 'supplier') {
      options = suppliers.map(s => ({ value: s.id, label: s.name }));
    } else if (formData.party_type === 'customer') {
      options = clients.map(c => ({ value: c.id, label: c.name }));
    } else if (formData.party_type === 'unit') {
      options = locations.map(l => ({ value: l.id, label: l.name }));
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12 text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="create-gate-pass-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/store/gate-pass')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create Gate Pass</h1>
          <p className="text-gray-500">Manual gate pass for outward goods</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Gate Pass Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => handleFormChange('category', v)}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                            {cat.is_returnable && (
                              <Badge variant="outline" className="ml-2 text-xs">Returnable</Badge>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location *</Label>
                    <Select 
                      value={formData.location_id} 
                      onValueChange={(v) => handleFormChange('location_id', v)}
                    >
                      <SelectTrigger data-testid="location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Party Type</Label>
                    <Select 
                      value={formData.party_type} 
                      onValueChange={(v) => handleFormChange('party_type', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer</SelectItem>
                        <SelectItem value="supplier">Supplier</SelectItem>
                        <SelectItem value="unit">Unit</SelectItem>
                        <SelectItem value="job_worker">Job Worker</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="party_name">Party Name *</Label>
                    {['supplier', 'customer', 'unit'].includes(formData.party_type) ? (
                      <SearchableSelect
                        options={getPartyOptions()}
                        value={formData.party_id}
                        onValueChange={handlePartySelect}
                        placeholder="Select party..."
                        searchPlaceholder="Search party..."
                        emptyText="No parties found"
                        data-testid="party-select"
                      />
                    ) : (
                      <Input
                        value={formData.party_name}
                        onChange={(e) => handleFormChange('party_name', e.target.value)}
                        placeholder="Enter party name"
                        data-testid="party-name-input"
                      />
                    )}
                  </div>
                </div>

                {formData.requires_return && (
                  <div>
                    <Label htmlFor="expected_return_date">Expected Return Date</Label>
                    <Input
                      type="date"
                      value={formData.expected_return_date}
                      onChange={(e) => handleFormChange('expected_return_date', e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="remarks">Remarks</Label>
                  <Textarea
                    value={formData.remarks}
                    onChange={(e) => handleFormChange('remarks', e.target.value)}
                    placeholder="Additional notes"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Items Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Items</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Item Name *</TableHead>
                      <TableHead className="w-[100px]">Quantity *</TableHead>
                      <TableHead className="w-[100px]">Unit</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Input
                            value={item.item_name}
                            onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}
                            placeholder="Item name"
                            data-testid={`item-name-${index}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            placeholder="Qty"
                            min="0"
                            step="0.01"
                            data-testid={`item-qty-${index}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={item.unit} 
                            onValueChange={(v) => handleItemChange(index, 'unit', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {uomOptions.map(uom => (
                                <SelectItem key={uom} value={uom}>{uom}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.remarks}
                            onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                            placeholder="Remarks"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            disabled={items.length === 1}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Transport & Documents */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Transport Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="vehicle_number">Vehicle Number</Label>
                  <Input
                    value={formData.vehicle_number}
                    onChange={(e) => handleFormChange('vehicle_number', e.target.value.toUpperCase())}
                    placeholder="MH04AB1234"
                    data-testid="vehicle-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="driver_name">Driver Name</Label>
                  <Input
                    value={formData.driver_name}
                    onChange={(e) => handleFormChange('driver_name', e.target.value)}
                    placeholder="Driver name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="driver_contact">Driver Contact</Label>
                  <Input
                    value={formData.driver_contact}
                    onChange={(e) => handleFormChange('driver_contact', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
                
                <div>
                  <Label htmlFor="transporter_name">Transporter</Label>
                  <Input
                    value={formData.transporter_name}
                    onChange={(e) => handleFormChange('transporter_name', e.target.value)}
                    placeholder="Transport company"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="dc_number">DC Number</Label>
                  <Input
                    value={formData.dc_number}
                    onChange={(e) => handleFormChange('dc_number', e.target.value)}
                    placeholder="Delivery Challan No."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={saving}
              data-testid="submit-gate-pass"
            >
              {saving ? (
                'Creating...'
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Gate Pass
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
    </DashboardLayout>
  );
};

export default CreateGatePassPage;
